import { formatPartialDate, fullName, type PartialDate } from '@gallarday/core';
import type { DocumentKind, EventType, LifeEvent, PersonLite, Role } from '../api/types.ts';
import { currentLocale, t } from '../i18n/index.ts';
import { labels } from '../i18n/labels.ts';

export const nameOf = (p: Pick<PersonLite, 'givenNames' | 'paternalSurname' | 'maternalSurname'>) => fullName(p);

/** "1858–1916", "1964–Vivo", "1836–Fallecida", "Fallecido"; si no se sabe si vive, "1934–?" (en el idioma activo). */
export function spanOf(p: Pick<PersonLite, 'birthYear' | 'deathYear' | 'isLiving' | 'sex'>): string {
  if (p.isLiving === null && !p.deathYear) return p.birthYear ? `${p.birthYear}–?` : t('Sin datos de vida');
  const living = p.sex === 'F' ? t('Viva') : t('Vivo');
  const dead = p.sex === 'F' ? t('Fallecida') : t('Fallecido');
  const end = p.isLiving ? living : p.deathYear ? String(p.deathYear) : dead;
  if (!p.birthYear) return p.isLiving ? living : p.deathYear ? `–${p.deathYear}` : dead;
  return `${p.birthYear}–${end}`;
}

const MONTHS: Record<'en' | 'fr' | 'de', readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
  de: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'],
};
const QUALIFIER: Record<'en' | 'fr' | 'de', Record<'about' | 'before' | 'after', string>> = {
  en: { about: 'c. ', before: 'before ', after: 'after ' },
  fr: { about: 'v. ', before: 'avant ', after: 'après ' },
  de: { about: 'um ', before: 'vor ', after: 'nach ' },
};

/** Fecha parcial ("16 ene 1857", "c. 1827") en el idioma activo; en español, igual que @gallarday/core. */
export function partialDate(d: PartialDate | null | undefined): string {
  const locale = currentLocale();
  if (locale === 'es') return formatPartialDate(d);
  if (!d || !d.year) return '';
  const parts: string[] = [];
  if (d.month && d.day) parts.push(locale === 'de' ? `${d.day}.` : String(d.day));
  if (d.month) parts.push(MONTHS[locale][d.month - 1] ?? '');
  parts.push(String(d.year));
  const q = d.qualifier && d.qualifier !== 'exact' ? QUALIFIER[locale][d.qualifier] : '';
  return q + parts.join(' ');
}

export const dateOf = (e: Pick<LifeEvent, 'year' | 'month' | 'day' | 'qualifier'>) =>
  partialDate({ year: e.year, month: e.month, day: e.day, qualifier: e.qualifier });

export const EVENT_LABEL = labels<EventType>({
  birth: 'Nacimiento',
  baptism: 'Bautizo',
  marriage: 'Matrimonio',
  divorce: 'Divorcio',
  death: 'Defunción',
  burial: 'Entierro',
  residence: 'Residencia',
  occupation: 'Ocupación',
  other: 'Otro acontecimiento',
  confirmation: 'Confirmación',
  first_communion: 'Primera comunión',
  education: 'Estudios',
  graduation: 'Graduación',
  emigration: 'Emigración',
  immigration: 'Inmigración',
  naturalization: 'Naturalización',
  retirement: 'Jubilación',
  cremation: 'Cremación',
  adoption: 'Adopción',
  civil_marriage: 'Matrimonio civil',
  religious_marriage: 'Matrimonio religioso',
});

export const DOC_LABEL = labels<DocumentKind>({
  birth_cert: 'Partida de nacimiento',
  baptism_cert: 'Partida de bautizo',
  confirmation_cert: 'Partida de confirmación',
  marriage_cert: 'Partida de matrimonio',
  civil_marriage_cert: 'Partida de matrimonio civil',
  religious_marriage_cert: 'Partida de matrimonio religioso',
  death_cert: 'Partida de defunción',
  photo: 'Fotografía',
  other: 'Otro documento',
});

/** Tipos que se ofrecen al subir o reclasificar: la partida de matrimonio genérica solo se conserva si ya lo era. */
export const docKindOptions = (current?: DocumentKind): DocumentKind[] =>
  (Object.keys(DOC_LABEL) as DocumentKind[]).filter((k) => k !== 'marriage_cert' || current === 'marriage_cert');

export const ROLE_LABEL = labels<Role>({
  viewer: 'Lector',
  editor: 'Colaborador',
  admin: 'Administrador',
  owner: 'Propietario',
});

export const ROLE_HELP = labels<Role>({
  viewer: 'Puede ver el árbol, los documentos y el historial.',
  editor: 'Además puede agregar y corregir personas, relaciones y documentos.',
  admin: 'Además invita familiares, gestiona miembros, importa GEDCOM y deshace cambios de otros.',
  owner: 'Control total, incluida la eliminación del árbol.',
});

const RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };
export const can = (role: Role | undefined, min: Role) => !!role && RANK[role] >= RANK[min];

export function timeAgo(iso: string): string {
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' });
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
  for (const [unit, secs] of units) if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  return t('hace un momento');
}

export const dateTime = (iso: string) =>
  new Intl.DateTimeFormat(currentLocale(), { dateStyle: 'long', timeStyle: 'short' }).format(new Date(iso));

export const longDate = (iso: string) => new Intl.DateTimeFormat(currentLocale(), { dateStyle: 'long' }).format(new Date(iso));

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

/** Color de la línea familiar según sexo. */
export const sexColor = (s: PersonLite['sex']) => (s === 'M' ? 'var(--male)' : s === 'F' ? 'var(--female)' : 'var(--ink-faint)');
